import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { signIn, signOut, getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import type { User } from '../types/user';

interface AuthUser extends User {
  tenantId: string;
  isSuperAdmin?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: AuthUser) => void;
  setToken: (token: string) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        try {
          console.log('Starting login process...');
          
          // Check if user is already signed in and force sign out first to prevent the UserAlreadyAuthenticatedException
          try {
            const currentSession = await fetchAuthSession();
            if (currentSession.tokens) {
              console.log('User already has an active session, signing out first...');
              await signOut({ global: true });
              // Small delay to ensure signout completes
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          } catch (e) {
            // Ignore errors here - just means user is not authenticated
          }
          
          // Sign in with AWS Cognito
          const { isSignedIn } = await signIn({
            username: email,
            password: password,
          });

          console.log('Sign in result:', { isSignedIn });

          if (isSignedIn) {
            // Get user info, session and attributes
            const user = await getCurrentUser();
            const session = await fetchAuthSession();
            const userAttributes = await fetchUserAttributes();
            const token = session.tokens?.idToken?.toString();

            console.log('User attributes:', userAttributes);
            console.log('Session tokens:', {
              hasAccessToken: !!session.tokens?.accessToken,
              hasIdToken: !!session.tokens?.idToken,
              usingIdToken: true
            });

            const userData: AuthUser = {
              id: user.userId,
              email: userAttributes.email || user.signInDetails?.loginId || email,
              firstName: userAttributes.given_name || 'User',
              lastName: userAttributes.family_name || '',
              role: (userAttributes['custom:role'] as any) || 'user',
              status: 'active',
              tenantId: userAttributes['custom:tenant_id'] || 'default',
              isSuperAdmin: userAttributes['custom:is_super_admin'] === 'true',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

            console.log('Setting user data:', userData);

            // Update state immediately
            set({
              user: userData,
              token: token || null,
              isAuthenticated: true,
            });

            console.log('Login completed successfully');
          } else {
            throw new Error('Sign in was not successful');
          }
        } catch (error) {
          console.error('Login error:', error);
          // Ensure auth state is cleared on error
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          console.log('Starting logout process...');
          // Use the global signout parameter to clear all devices
          await signOut({ global: true });
          console.log('AWS Cognito signOut completed');
          
          // Clear any local storage items that might be keeping state
          if (typeof window !== 'undefined') {
            // Clear specific Amplify items
            const authKeys = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key?.includes('amplify') || key?.includes('auth') || key?.includes('token')) {
                authKeys.push(key);
              }
            }
            
            // Remove identified auth keys
            authKeys.forEach(key => {
              console.log('Removing localStorage key:', key);
              localStorage.removeItem(key);
            });

            // Also clear session storage items
            sessionStorage.clear();
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Always reset state regardless of errors
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          console.log('Auth state reset completed');
          
          // Force a small delay to ensure all cleanup is complete
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      },

      setUser: (user: AuthUser) => {
        set({ user });
      },

      setToken: (token: string) => {
        set({ token });
      },

      checkAuth: async () => {
        try {
          const user = await getCurrentUser();
          const session = await fetchAuthSession();
          const userAttributes = await fetchUserAttributes();
          const token = session.tokens?.idToken?.toString();

          if (user && token) {
            const userData: AuthUser = {
              id: user.userId,
              email: userAttributes.email || user.signInDetails?.loginId || '',
              firstName: userAttributes.given_name || 'User',
              lastName: userAttributes.family_name || '',
              role: (userAttributes['custom:role'] as any) || 'user',
              status: 'active',
              tenantId: userAttributes['custom:tenant_id'] || 'default',
              isSuperAdmin: userAttributes['custom:is_super_admin'] === 'true',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

            set({
              user: userData,
              token: token,
              isAuthenticated: true,
            });
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
            });
          }
        } catch (error) {
          console.error('Auth check error:', error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);